/**
 * Composable per Cross Company Target Job Activities autocomplete con loading e error handling centralizzati
 */

import { ref, readonly } from 'vue';
import { useODataBuilder } from '../useODataBuilder';
import { useLoadingState } from '../useLoadingState';
import { useNotification } from '../useNotification';
import { useStaleSafeRequest } from '../useStaleSafeRequest';
import type { BaseAutocompleteComposableOptions } from './types';
import type {
	CrossCompanyTargetJobActivitiesOptions,
	AutocompleteResult,
} from '@/lib/autocompletes';
import { getCrossCompanyTargetJobActivitiesAutocomplete } from '@/lib/autocompletes/crossCompanyTargetJobActivities';
// TODO: Usare CrossCompanyTargetJobActivityAutocompleteResult quando sarà creato

export function useCrossCompanyTargetJobActivitiesAutocomplete(
	options: BaseAutocompleteComposableOptions = {},
) {
	const targetJobActivities = ref<AutocompleteResult[]>([]);

	const { buildOdataQuery } = useODataBuilder();
	const withLoadingFn = options.withLoading || useLoadingState().withLoading;
	const { error } = useNotification();
	const { requestToken } = useStaleSafeRequest();

	async function searchCrossCompanyTargetJobActivities(
		options: CrossCompanyTargetJobActivitiesOptions,
	): Promise<void> {
		const token = requestToken();
		await withLoadingFn(async () => {
			try {
				const result = await getCrossCompanyTargetJobActivitiesAutocomplete(
					buildOdataQuery,
					options,
				);
				if (token.isLatest()) {
					targetJobActivities.value = result;
				}
			} catch (err) {
				console.error(
					'❌ ERROR useCrossCompanyTargetJobActivitiesAutocomplete.searchCrossCompanyTargetJobActivities:',
					err,
				);
				error(
					'Errore durante il caricamento delle attività cross-company! Ricaricare la pagina e riprovare.',
				);
				if (token.isLatest()) {
					targetJobActivities.value = [];
				}
			}
		}, 'searchCrossCompanyTargetJobActivities');
	}

	function clearTargetJobActivities(): void {
		targetJobActivities.value = [];
	}

	return {
		targetJobActivities: readonly(targetJobActivities),
		searchCrossCompanyTargetJobActivities,
		clearTargetJobActivities,
	};
}
